import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { Plus, FolderOpen, ChevronRight, ChevronDown, Edit, Trash2, ArrowUp, ArrowDown, GripVertical, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const CategoriesPage = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState('');
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [reorderMode, setReorderMode] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      const cats = res.data || [];
      // Sort by order
      cats.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(cats);
      setCategoryTree(buildTree(cats));
      
      const expandState = {};
      cats.forEach(cat => {
        expandState[cat.id] = true;
      });
      setExpanded(expandState);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (cats) => {
    const catMap = {};
    cats.forEach(cat => {
      cat.children = [];
      cat.level = 0;
      catMap[cat.id] = cat;
    });

    cats.forEach(cat => {
      let level = 0;
      let current = cat;
      while (current.parent_id && catMap[current.parent_id]) {
        level++;
        current = catMap[current.parent_id];
      }
      cat.level = level;
    });

    const tree = [];
    cats.forEach(cat => {
      if (cat.parent_id && catMap[cat.parent_id]) {
        catMap[cat.parent_id].children.push(cat);
      } else {
        tree.push(cat);
      }
    });

    // Sort children by order
    const sortChildren = (items) => {
      items.sort((a, b) => (a.order || 0) - (b.order || 0));
      items.forEach(item => {
        if (item.children.length > 0) {
          sortChildren(item.children);
        }
      });
    };
    sortChildren(tree);

    return tree;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    if (parentId) {
      const parent = categories.find(c => c.id === parentId);
      if (parent && parent.level >= 2) {
        toast.error(t('maxNestingReached'));
        return;
      }
    }

    setCreating(true);
    try {
      const siblings = categories.filter(c => c.parent_id === (parentId || null));
      const maxOrder = siblings.reduce((max, c) => Math.max(max, c.order || 0), 0);
      
      await axios.post(`${API_URL}/categories`, {
        name,
        description,
        parent_id: parentId || null,
        order: maxOrder + 1
      });
      toast.success(t('categoryCreated'));
      setDialogOpen(false);
      setName('');
      setDescription('');
      setParentId('');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description || '');
    setParentId(category.parent_id || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await axios.put(`${API_URL}/categories/${editingCategory.id}`, {
        name,
        description,
        parent_id: parentId || null
      });
      toast.success('Category updated');
      setEditDialogOpen(false);
      setEditingCategory(null);
      setName('');
      setDescription('');
      setParentId('');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${API_URL}/categories/${categoryId}`);
      toast.success(t('categoryDeleted'));
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const moveCategory = (categoryId, direction, siblings) => {
    const index = siblings.findIndex(c => c.id === categoryId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === siblings.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newSiblings = [...siblings];
    [newSiblings[index], newSiblings[newIndex]] = [newSiblings[newIndex], newSiblings[index]];

    // Update orders
    newSiblings.forEach((cat, idx) => {
      cat.order = idx;
    });

    // Update the tree
    setCategories(prev => {
      const updated = prev.map(c => {
        const sibling = newSiblings.find(s => s.id === c.id);
        if (sibling) {
          return { ...c, order: sibling.order };
        }
        return c;
      });
      updated.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategoryTree(buildTree(updated));
      return updated;
    });

    setHasOrderChanges(true);
  };

  const saveOrder = async () => {
    try {
      const orders = categories.map(c => ({ id: c.id, order: c.order || 0 }));
      await axios.post(`${API_URL}/categories/reorder`, orders);
      toast.success('Category order saved');
      setHasOrderChanges(false);
      setReorderMode(false);
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  const toggleExpand = (catId) => {
    setExpanded(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const getSiblings = (category) => {
    if (category.parent_id) {
      return categories.filter(c => c.parent_id === category.parent_id);
    }
    return categoryTree;
  };

  const renderCategory = (category, siblings) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expanded[category.id];
    const siblingIndex = siblings.findIndex(c => c.id === category.id);
    const canMoveUp = siblingIndex > 0;
    const canMoveDown = siblingIndex < siblings.length - 1;

    return (
      <div key={category.id} className="mb-2">
        <div 
          className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
            reorderMode ? 'bg-blue-50 border-blue-200' : 'bg-white border-zinc-200 hover:bg-zinc-50'
          }`}
          style={{ marginLeft: `${category.level * 24}px` }}
        >
          {reorderMode && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => moveCategory(category.id, 'up', siblings)}
                disabled={!canMoveUp}
              >
                <ArrowUp className={`h-4 w-4 ${canMoveUp ? 'text-blue-600' : 'text-zinc-300'}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => moveCategory(category.id, 'down', siblings)}
                disabled={!canMoveDown}
              >
                <ArrowDown className={`h-4 w-4 ${canMoveDown ? 'text-blue-600' : 'text-zinc-300'}`} />
              </Button>
            </div>
          )}

          {hasChildren ? (
            <button
              onClick={() => toggleExpand(category.id)}
              className="p-1 hover:bg-zinc-100 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-zinc-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-zinc-500" />
              )}
            </button>
          ) : (
            <div className="w-6" />
          )}

          <FolderOpen className="h-5 w-5 text-violet-500" />
          
          <div className="flex-1">
            <div className="font-medium text-zinc-900">{category.name}</div>
            {category.description && (
              <div className="text-sm text-zinc-500">{category.description}</div>
            )}
          </div>

          <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
            Level {category.level + 1}
          </span>

          {!reorderMode && (
            <div className="flex items-center gap-1">
              {/* Add sub-category button - only show if level < 2 */}
              {category.level < 2 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setParentId(category.id);
                    setName('');
                    setDescription('');
                    setDialogOpen(true);
                  }}
                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="Add sub-category"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(category)}
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4 text-zinc-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(category.id)}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="mt-2">
            {category.children.map(child => renderCategory(child, category.children))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-600">Loading categories...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-violet-100 flex items-center justify-center">
              <FolderOpen className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('categories')}
              </h1>
              <p className="text-sm text-zinc-600">{t('categoriesDescription')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {reorderMode ? (
              <>
                <Button variant="outline" onClick={() => { setReorderMode(false); fetchCategories(); }}>
                  Cancel
                </Button>
                <Button onClick={saveOrder} disabled={!hasOrderChanges}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Order
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setReorderMode(true)}>
                  <GripVertical className="h-4 w-4 mr-2" />
                  Reorder
                </Button>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addCategory')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Reorder Mode Banner */}
        {reorderMode && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700 font-medium">
              <GripVertical className="h-4 w-4 inline-block mr-2" />
              Reorder Mode: Use the arrows to move categories up or down. Click "Save Order" when done.
            </p>
          </div>
        )}

        {/* Categories List */}
        {categoryTree.length === 0 ? (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-lg">
            <FolderOpen className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-600 mb-4">{t('noCategories')}</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('createFirstCategory')}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {categoryTree.map(cat => renderCategory(cat, categoryTree))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('addCategory')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>{t('categoryName')}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter category name"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{t('description')}</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{t('parentCategory')}</Label>
                <select
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-zinc-200 rounded-md"
                >
                  <option value="">{t('none')}</option>
                  {categories.filter(c => c.level < 2).map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {'—'.repeat(cat.level)} {cat.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-zinc-500 mt-1">{t('maxThreeLevels')}</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating...' : t('create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('categoryName')}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter category name"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{t('description')}</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{t('parentCategory')}</Label>
                <select
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-zinc-200 rounded-md"
                >
                  <option value="">{t('none')}</option>
                  {categories.filter(c => c.id !== editingCategory?.id && c.level < 2).map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {'—'.repeat(cat.level)} {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleSaveEdit}>
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CategoriesPage;
